import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  LayoutGrid,
  Tag,
  Workflow,
  HeartHandshake,
  Briefcase,
  MessageSquare,
  Stethoscope,
  Scissors,
  Sparkles,
  FlaskConical,
  Flower2,
  ScanFace,
  UserCog,
  UserCheck,
  Shield,
  Building2,
  Wrench,
  List,
  // ShoppingBag, Package, // dùng cho nhóm Sản phẩm đang ẩn
  Calendar,
  Building,
  MessageCircle,
  Megaphone,
  BarChart3,
  PieChart,
  FileBarChart,
  TrendingUp,
  Syringe,
  FolderOpen,
  CalendarDays,
  MessageSquareText,
  // NotebookText, // dùng cho mục Danh sách bán hàng đang ẩn
  ClipboardCheck,
  HandHeart,
  Radio,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from './ui/sidebar';
import { NavDocuments } from './NavDocuments';
import styles from './AppSidebar.module.scss';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../../store/authStore';
import { canAccess } from '../../config/permissions';

interface BaseMenuItem {
  title: string;
  label?: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface RegularMenuItem extends BaseMenuItem {
  type: 'item';
  label?: string;
  url: string;
  activePrefixes?: string[];
}

interface CollapsibleMenuItem extends BaseMenuItem {
  type: 'group';
  label?: string;
  items: {
    title: string;
    url: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}

type MenuItem = RegularMenuItem | CollapsibleMenuItem;

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> { }

export function AppSidebar({ ...props }: AppSidebarProps) {
  const { state } = useSidebar();
  const { t } = useTranslation();
  const { permissions, isSuperAdmin } = useAuthStore();

  // Admin menu items - có thêm page cho admin thì thêm nav ở đây
  const adminMenuItems: MenuItem[] = [
    {
      type: 'item',
      title: t('sidebar.overview'),
      url: '/',
      icon: LayoutDashboard,
    },
    {
      label: t('sidebar.label_cms'),
      type: 'item',
      title: t('sidebar.chats'),
      url: '/chats',
      icon: MessageCircle,
    },
    {
      type: 'item',
      title: t('sidebar.customers'),
      url: '/customer',
      icon: Users,
    },
    {
      type: 'item',
      title: t('sidebar.customers_demo'),
      url: '/customer-demo',
      icon: Users,
    },
    {
      type: 'item',
      title: t('sidebar.orders'),
      url: '/appointments',
      icon: CalendarDays,
      activePrefixes: ['/appointment/detail/', '/appointment/create'],
    },
    {
      type: 'item',
      title: t('sidebar.consultation'),
      url: '/consultations',
      icon: MessageSquareText,
      activePrefixes: ['/consultation/detail/'],
    },
    {
      type: 'item',
      title: t('sidebar.execution'),
      url: '/execution-schedule',
      icon: ClipboardCheck,
    },
    {
      type: 'item',
      title: t('sidebar.care_list'),
      url: '/care-list',
      icon: HandHeart,
    },
    // Tạm ẩn trang Danh sách bán hàng
    // {
    //   type: 'item',
    //   title: t('sidebar.list_order'),
    //   url: '/list-order',
    //   icon: NotebookText,
    // },
    {
      type: 'group',
      title: t('sidebar.specialty'),
      icon: Stethoscope,
      items: [
        {
          title: t('sidebar.hair_clinic'),
          url: '/hair-clinic',
          icon: Scissors,
        },
        {
          title: t('sidebar.miko_reception'),
          url: '/miko-reception',
          icon: Building2,
        },
        {
          title: t('sidebar.cosmetic_surgery'),
          url: '/cosmetic-surgery-reception',
          icon: Syringe,
        },
        {
          title: t('sidebar.thread_lift'),
          url: '/thread-lift-reception',
          icon: Sparkles,
        },
        {
          title: t('sidebar.ozempic'),
          url: '/consultation-form',
          icon: FlaskConical,
        },
        {
          title: t('sidebar.beauty_consultation'),
          url: '/beauty-consultation',
          icon: Flower2,
        },
        {
          title: t('sidebar.skin_consultation'),
          url: '/consultation-tabs',
          icon: ScanFace,
        },
      ],
    },
    {
      type: 'group',
      title: t('sidebar.categories'),
      icon: LayoutGrid,
      items: [
        {
          title: t('sidebar.sale_tags'),
          url: '/customer-tags',
          icon: Tag,
        },
        {
          title: t('sidebar.appointment_transitions'),
          url: '/appointment-transitions',
          icon: Workflow,
        },
        {
          title: t('sidebar.chat_tags'),
          url: '/care-status',
          icon: HeartHandshake,
        },
        {
          title: t('sidebar.professions'),
          url: '/profession',
          icon: Briefcase,
        },
        {
          title: t('sidebar.quick_messages'),
          url: '/quick-messages',
          icon: MessageSquare,
        },
      ],
    },

    {
      type: 'group',
      title: t('sidebar.employees'),
      icon: UserCog,
      items: [
        {
          title: t('sidebar.employees'),
          url: '/employee',
          icon: UserCheck,
        },
        {
          title: t('sidebar.group_roles'),
          url: '/group-roles',
          icon: Shield,
        },
        {
          title: t('sidebar.departments'),
          url: '/departments',
          icon: Building2,
        },
      ],
    },
    {
      type: 'group',
      title: t('sidebar.services'),
      icon: Wrench,
      items: [
        {
          title: t('sidebar.service_categories'),
          url: '/services',
          icon: FolderOpen,
        },
        {
          title: t('sidebar.service_list'),
          url: '/services-list',
          icon: List,
        },
      ],
    },
    // Tạm ẩn nhóm Sản phẩm (Danh mục sản phẩm, Sản phẩm)
    // {
    //   type: 'group',
    //   title: t('sidebar.products'),
    //   icon: ShoppingBag,
    //   items: [
    //     {
    //       title: t('sidebar.product_categories'),
    //       url: '/products-cat',
    //       icon: FolderOpen,
    //     },
    //     {
    //       title: t('sidebar.products'),
    //       url: '/products',
    //       icon: Package,
    //     },
    //   ],
    // },

    // {
    //   type: 'item',
    //   title: t('sidebar.advertise'),
    //   url: '/advertise',
    //   icon: Megaphone,
    // },
    // {
    //   type: 'item',
    //   title: t('sidebar.AdvertisingCampaignManagement'),
    //   url: '/advertising-campaign-management',
    //   icon: Megaphone,
    // },
    // {
    //   type: 'item',
    //   title: t('sidebar.AdvertisingCampaign'),
    //   url: '/advertising-campaign',
    //   icon: Megaphone,
    // },
    // {
    //   type: 'item',
    //   title: t('sidebar.calendar'),
    //   url: '/calendar',
    //   icon: Calendar,
    // },
    {
      type: 'group',
      title: t('sidebar.marketing_campaign_report'),
      icon: Megaphone,
      items: [
        {
          title: t('sidebar.reports'),
          url: '/marketing-campaign-report',
          icon: PieChart,
        },
        {
          title: t('sidebar.AdvertisingCampaignManagement'),
          url: '/advertising-campaign-management',
          icon: Megaphone,
        },
        {
          title: t('sidebar.AdvertisingCampaign'),
          url: '/advertising-campaign',
          icon: Megaphone,
        },
      ],
    },
    {
      type: 'group',
      title: t('sidebar.reports'),
      icon: BarChart3,
      items: [
        // {
        //   title: t('sidebar.overview_report'),
        //   url: '/overview-report',
        //   icon: PieChart,
        // },
        // {
        //   title: t('sidebar.page_report'),
        //   url: '/page-report',
        //   icon: FileBarChart,
        // },
        {
          title: t('sidebar.sale_online_report'),
          url: '/sale-online-report',
          icon: TrendingUp,
        },
        {
          title: t('sidebar.customer_report'),
          url: '/customer-report',
          icon: UserCheck,
        },
        {
          title: t('sidebar.marketing_report'),
          url: '/marketing-report',
          icon: UserCheck,
        },
      ],
    },
    {
      type: 'item',
      title: t('sidebar.branch_manager'),
      url: '/branch-manager',
      icon: Building,
    },
    {
      type: 'item',
      title: t('Trực Page'),
      url: '/dashbroad-truc-page',
      icon: Radio,
    },
  ];

  // Lọc menu theo permissions (super admin thấy toàn bộ).
  const visibleMenuItems = adminMenuItems.filter((item) => {
    if (item.type === 'item') {
      return canAccess(item.url, permissions, isSuperAdmin);
    }
    if (item.type === 'group') {
      const visibleSub = item.items.filter((sub) => canAccess(sub.url, permissions, isSuperAdmin));
      return visibleSub.length > 0;
    }
    return true;
  }).map((item) => {
    if (item.type === 'group') {
      return {
        ...item,
        items: item.items.filter((sub) => canAccess(sub.url, permissions, isSuperAdmin)),
      };
    }
    return item;
  });

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        styles.appSidebar,
        state === 'collapsed' ? styles.collapsed : styles.expanded
      )}
      {...props}
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <Link to="/" className={styles.headerLink}>
              <div className="headerContent w-full flex items-center justify-center">
                {state === 'collapsed' && (
                  <div className="w-10 h-10 flex items-center justify-center">
                    <img src="/images/logo-v.png" alt="logo" className='w-8 md:w-10 h-8 md:h-10 object-contain' />
                  </div>
                )}
                {state === 'expanded' && (
                  <div className="w-full flex justify-center">
                    <img src="/images/logo.png" alt="logo" className='h-[50px] object-contain' />
                  </div>
                )}
              </div>
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="sidebarContent">
        <NavDocuments items={visibleMenuItems} />
      </SidebarContent>
    </Sidebar>
  );
}
